import { useCallback, useMemo, useState } from "react";
import { formatTime } from "@tellescope/utils";

import { parseTimeString } from "./parseTimeString";

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

const timeStringToSeconds = (time: string): number | undefined => {
  const parts = time.split(":").map(Number);
  if (parts.some((p) => !Number.isFinite(p))) return undefined;
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

interface TimeParts {
  hour: number;
  minute: number;
  second: number;
}

const parseValueToParts = (value: string | undefined): TimeParts | undefined => {
  if (!value) return undefined;
  const seconds = timeStringToSeconds(value);
  if (seconds === undefined) return undefined;
  const parts = value.split(":").map(Number);
  return {
    hour: parts[0] ?? 0,
    minute: parts[1] ?? 0,
    second: parts[2] ?? 0,
  };
};

const HOUR_ORDER_12H = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const display12From24 = (h24: number) => {
  const mod = h24 % 12;
  return mod === 0 ? 12 : mod;
};

const to24From12 = (displayHour: number, period: "AM" | "PM"): number => {
  if (period === "AM") return displayHour === 12 ? 0 : displayHour;
  return displayHour === 12 ? 12 : displayHour + 12;
};

export interface UseTimeInputLogicProps {
  value: string | undefined;
  onChange: ((value: string) => void) | undefined;
  showSeconds: boolean;
  use24Hour: boolean;
  min: string | undefined;
  max: string | undefined;
  stepMinutes: number;
  stepSeconds: number;
}

export const useTimeInputLogic = ({
  value,
  onChange,
  showSeconds,
  use24Hour,
  min,
  max,
  stepMinutes,
  stepSeconds,
}: UseTimeInputLogicProps) => {
  const parts = useMemo(() => parseValueToParts(value), [value]);

  const minSec = useMemo(() => (min ? timeStringToSeconds(min) : undefined), [min]);
  const maxSec = useMemo(() => (max ? timeStringToSeconds(max) : undefined), [max]);

  const currentHour24 = parts?.hour ?? 0;
  const currentMinute = parts?.minute ?? 0;
  const currentSecond = parts?.second ?? 0;
  const currentPeriod: "AM" | "PM" = currentHour24 < 12 ? "AM" : "PM";
  const currentDisplayHour = display12From24(currentHour24);

  const formattedValue = useMemo(() => {
    if (!parts) return "";
    const date = new Date(2000, 0, 1, parts.hour, parts.minute, parts.second);
    if (use24Hour) {
      return formatTime(date, showSeconds ? "HH:mm:ss" : "HH:mm");
    }
    return formatTime(date, showSeconds ? "hh:mm:ss a" : "hh:mm a");
  }, [parts, use24Hour, showSeconds]);

  const emit = useCallback(
    (h24: number, m: number, s: number) => {
      const base = `${pad(h24)}:${pad(m)}`;
      onChange?.(showSeconds ? `${base}:${pad(s)}` : base);
    },
    [onChange, showSeconds]
  );

  const rangeOverlapsBounds = useCallback(
    (rangeStart: number, rangeEnd: number) => {
      if (minSec !== undefined && rangeEnd < minSec) return false;
      if (maxSec !== undefined && rangeStart > maxSec) return false;
      return true;
    },
    [minSec, maxSec]
  );

  const hourItems = useMemo(() => {
    if (use24Hour) {
      return Array.from({ length: 24 }, (_, h) => {
        const start = h * 3600;
        const end = start + 59 * 60 + 59;
        return {
          value: h,
          label: pad(h),
          disabled: !rangeOverlapsBounds(start, end),
        };
      });
    }
    return HOUR_ORDER_12H.map((displayH) => {
      const h24 = to24From12(displayH, currentPeriod);
      const start = h24 * 3600;
      const end = start + 59 * 60 + 59;
      return {
        value: displayH,
        label: String(displayH),
        disabled: !rangeOverlapsBounds(start, end),
      };
    });
  }, [use24Hour, currentPeriod, rangeOverlapsBounds]);

  const minuteItems = useMemo(() => {
    const items: { value: number; label: string; disabled: boolean }[] = [];
    for (let m = 0; m < 60; m += stepMinutes) {
      const start = currentHour24 * 3600 + m * 60;
      const end = start + 59;
      items.push({
        value: m,
        label: pad(m),
        disabled: !rangeOverlapsBounds(start, end),
      });
    }
    return items;
  }, [stepMinutes, currentHour24, rangeOverlapsBounds]);

  const secondItems = useMemo(() => {
    if (!showSeconds) return undefined;
    const items: { value: number; label: string; disabled: boolean }[] = [];
    for (let s = 0; s < 60; s += stepSeconds) {
      const total = currentHour24 * 3600 + currentMinute * 60 + s;
      items.push({
        value: s,
        label: pad(s),
        disabled: !rangeOverlapsBounds(total, total),
      });
    }
    return items;
  }, [showSeconds, stepSeconds, currentHour24, currentMinute, rangeOverlapsBounds]);

  const periodItems = useMemo(() => {
    if (use24Hour) return undefined;
    return (["AM", "PM"] as const).map((p) => {
      const h24 = to24From12(currentDisplayHour, p);
      const total = h24 * 3600 + currentMinute * 60 + currentSecond;
      return {
        value: p,
        label: p,
        disabled: !rangeOverlapsBounds(total, total),
      };
    });
  }, [use24Hour, currentDisplayHour, currentMinute, currentSecond, rangeOverlapsBounds]);

  const setDisplayHour = useCallback(
    (displayH: number) => {
      const h24 = use24Hour ? displayH : to24From12(displayH, currentPeriod);
      emit(h24, currentMinute, currentSecond);
    },
    [use24Hour, currentPeriod, currentMinute, currentSecond, emit]
  );

  const setMinute = useCallback(
    (m: number) => emit(currentHour24, m, currentSecond),
    [currentHour24, currentSecond, emit]
  );

  const setSecond = useCallback(
    (s: number) => emit(currentHour24, currentMinute, s),
    [currentHour24, currentMinute, emit]
  );

  const setPeriod = useCallback(
    (p: "AM" | "PM") => {
      const h24 = to24From12(currentDisplayHour, p);
      emit(h24, currentMinute, currentSecond);
    },
    [currentDisplayHour, currentMinute, currentSecond, emit]
  );

  const [isFocused, setIsFocused] = useState(false);
  const [typedValue, setTypedValue] = useState<string | null>(null);

  const handleInputChange = useCallback(
    (next: string) => {
      setTypedValue(next);
      const parsed = parseTimeString(next, { showSeconds });
      if (parsed) onChange?.(parsed);
    },
    [onChange, showSeconds]
  );

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
    setTypedValue(formattedValue);
  }, [formattedValue]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    if (typedValue !== null && typedValue.trim() === "" && value) {
      onChange?.("");
    }
    setTypedValue(null);
  }, [typedValue, value, onChange]);

  const displayValue = isFocused && typedValue !== null ? typedValue : formattedValue;

  return {
    displayValue,
    hourItems,
    minuteItems,
    secondItems,
    periodItems,
    selectedDisplayHour: parts ? currentDisplayHour : undefined,
    selectedMinute: parts ? currentMinute : undefined,
    selectedSecond: parts ? currentSecond : undefined,
    selectedPeriod: parts ? currentPeriod : undefined,
    setDisplayHour,
    setMinute,
    setSecond,
    setPeriod,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
  };
};
